'use client';

import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useTranslation } from 'next-i18next/pages';

import { extractErrorMessage } from './utils';

// Define a type for API responses that may contain an error
interface ApiResponse<T> {
  error?: FetchBaseQueryError | SerializedError;
  data?: T;
}

interface ErrorHandlerOptions {
  errorTitle?: string;
  defaultErrorMessage?: string;
  showErrorNotification?: (params: { title: string; message: string }) => void;
}

export interface ApiResult<T> {
  success?: boolean;
  error?: FetchBaseQueryError | SerializedError | Error;
  data?: T;
}

/**
 * ### useHandleApiCall
 *
 * @description
 * A helper method for async lazy api calls that happen outside of the usual
 * RTK query hook flow. For example, API calls that happen in response to user
 * interaction or other events.
 *
 * @example
 * import { showErrorNotification } from '@highlight-cp/core-ui';
 * import { useHandleApiCall } from '@highlight-cp/hooks';
 *
 * ...
 *
 * const { handleApiCall } = useHandleApiCall();
 *
 * const handleSubmit = async () => {
 *   const { success, data, error } = await handleApiCall(
 *     postData(payload),
 *     {
 *       errorTitle: 'Operation Failed',
 *       defaultErrorMessage: 'Failed to submit data. Please try again later.'
 *       showErrorNotification,
 *     }
 *   );
 *
 *   if (success) {
 *     // Handle success case with result.data
 *     console.log(data);
 *   }
 *   else if (error) {
 *     // Handle error case with error
 *     console.log(error);
 *   }
 * };
 *
 * @returns Object containing the handleApiCall function
 */
export const useHandleApiCall = () => {
  const { t } = useTranslation();

  async function handleApiCall<T>(
    apiPromise: Promise<ApiResponse<T>>,
    options: ErrorHandlerOptions = {},
  ): Promise<ApiResult<T>> {
    const {
      errorTitle = t('Error'),
      defaultErrorMessage = t('An unexpected error occurred. Please try again later.'),
      showErrorNotification,
    } = options;

    try {
      const result = await apiPromise;

      // Check if the result contains an error property (RTK Query pattern)
      if (result && 'error' in result && result.error) {
        const errorMessage = extractErrorMessage(result.error, defaultErrorMessage);

        showErrorNotification?.({
          title: errorTitle,
          message: errorMessage,
        });

        return { success: false, error: result.error };
      }

      // For unwrapped results or other success patterns
      return { success: true, data: result.data as T };
    } catch (err) {
      const error = err as Error;
      const errorMessage = extractErrorMessage(error, defaultErrorMessage);

      showErrorNotification?.({
        title: errorTitle,
        message: errorMessage,
      });

      return { success: false, error };
    }
  }

  return { handleApiCall };
};
